import { Link } from "react-router-dom";
import useCasesData from "./useCasesData";
import Seo from "../Seo/Seo";
import { siteConfig } from "../../config/site";
import { useIsDanish } from "../../i18n/LanguageContext";

const tagColors = {
  Migration: "#dbeafe",
  Governance: "#ede9fe",
  IaC: "#dcfce7",
  Azure: "#e0f2fe",
  AI: "#fce7f3",
  Compliance: "#fef3c7",
  Security: "#fee2e2",
  "CI/CD": "#dcfce7",
  DevOps: "#e0f2fe",
  Automation: "#ede9fe",
  "Power Platform": "#fce7f3",
  "Low-code": "#fef3c7",
  Operations: "#f1f5f9",
  Identity: "#fce7f3",
  "Entra ID": "#ede9fe",
  Oracle: "#fff3e0",
  Networking: "#e0f2fe",
  Performance: "#dcfce7",
  Architecture: "#ede9fe",
  "Event-driven": "#e0f2fe",
  Observability: "#dcfce7",
};
const tagText = {
  Migration: "#1d4ed8", Governance: "#6d28d9", IaC: "#15803d",
  Azure: "#0369a1", AI: "#be185d", Compliance: "#92400e",
  Security: "#b91c1c", "CI/CD": "#15803d", DevOps: "#0369a1",
  Automation: "#6d28d9", "Power Platform": "#be185d", "Low-code": "#92400e",
  Operations: "#475569", Identity: "#be185d", "Entra ID": "#6d28d9",
  Oracle: "#b45309", Networking: "#0369a1", Performance: "#15803d",
  Architecture: "#6d28d9", "Event-driven": "#0369a1", Observability: "#15803d",
};

const useCasesDa = {
  "azure-migration-danish-sme": {
    title: "400+ servere flyttet til Azure for dansk forsyningsorganisation",
    category: "Azure Governance",
    summary: "En stor dansk forsynings- og affaldsorganisation flyttede over 400 servere til Azure og fik landing zone, governance, identitet og drift samlet i en kontrolleret cloudplatform.",
    metrics: [
      { label: "Servere migreret", value: "400+" },
      { label: "Lokale serverrum", value: "Fjernet" },
      { label: "Sektorposition", value: "Tidligt ude" },
      { label: "Identitetssystem", value: "Entra ID" },
    ],
  },
  "ai-guardrails-financial-services": {
    title: "AI-guardrails for finansiel virksomhed",
    category: "AI Governance",
    summary: "Microsoft Copilot og Azure OpenAI blev rullet ud på tværs af forretningen med governance, databeskyttelse, logging og kontroller fra dag ét.",
    metrics: [
      { label: "Forretningsområder live", value: "3" },
      { label: "Registrerede datahændelser", value: "0" },
      { label: "Compliance sign-off", value: "3 uger" },
      { label: "AI-interaktioner logget", value: "100%" },
    ],
  },
  "cicd-transformation-azure-devops": {
    title: "CI/CD-transformation med Azure DevOps",
    category: "Softwareudvikling",
    summary: "Manuelle FTP- og publish-profile deployments blev erstattet af automatiserede pipelines, approval gates, rollback og en mere kontrolleret releaseproces.",
    metrics: [
      { label: "Releasecyklus", value: "2 dage" },
      { label: "Deployment-frekvens", value: "3×" },
      { label: "Deployment-skabte incidents", value: "0" },
      { label: "Rollback-tid", value: "< 2 min" },
    ],
  },
  "power-platform-operations-automation": {
    title: "Governance for produktionsadgang med Access Packages",
    category: "Azure Governance",
    summary: "Adgang til grupper, shared mailboxes og produktionsressourcer blev gjort request-baseret, godkendt, auditerbar og automatisk opryddet efter politik.",
    metrics: [
      { label: "Adgangsmodel", value: "Policy-baseret" },
      { label: "Auditability", value: "Fuld" },
      { label: "Ejerskab", value: "Afdelingsstyret" },
      { label: "Oprydning", value: "Automatisk" },
    ],
  },
  "entra-id-rollout-200-person-org": {
    title: "Entra ID rollout i organisation med 200 brugere",
    category: "Azure Governance",
    summary: "Seks identitetssystemer blev samlet i ét, MFA blev håndhævet, og onboarding/offboarding blev automatiseret gennem Entra ID og lifecycle-processer.",
    metrics: [
      { label: "Identitetssystemer", value: "6 → 1" },
      { label: "MFA", value: "100%" },
      { label: "Offboarding", value: "15 min" },
      { label: "Helpdesk tickets", value: "−70%" },
    ],
  },
  "event-driven-utility-platform": {
    title: "Sikker event-driven platform til forsyningsdrift",
    category: "Microsoft Azure",
    summary: "Web services, daemons, events, databaser, storage og API'er blev samlet i et credential-free Azure-mønster med observability og sikkerhed fra start.",
    metrics: [
      { label: "Serviceauth", value: "Ingen indlejrede credentials" },
      { label: "Arkitektur", value: "Event-driven" },
      { label: "API-eksponering", value: "Centraliseret" },
      { label: "Observability", value: "Indbygget" },
    ],
  },
  "oracle-azure-latency-reduction": {
    title: "Lavere latency mellem Azure og Oracle Exascale",
    category: "Microsoft Azure",
    summary: "Latency mod Oracle Database@Azure Exascale blev reduceret ved at koordinere compute, Oracle-infrastruktur og netværksplacering på tværs af flere lag.",
    metrics: [
      { label: "Gns. Oracle latency", value: "~1 ms" },
      { label: "Lag koordineret", value: "3" },
      { label: "Deployment-model", value: "IaC" },
      { label: "Placement-strategi", value: "Prod-klar" },
    ],
  },
  "api-centralisation-azure-apim": {
    title: "Spredte API'er samlet bag én sikker gateway",
    category: "Azure Governance",
    summary: "API'er på tværs af on-prem, cloud og tredjepart blev samlet bag Azure API Management, Application Gateway og WAF med fælles auth, policies og logging.",
    metrics: [
      { label: "API'er samlet", value: "Alle" },
      { label: "Direkte backend-eksponering", value: "Ingen" },
      { label: "Auth", value: "Gateway-niveau" },
      { label: "WAF-regler", value: "OWASP Top 10" },
    ],
  },
};

export default function UseCasesPage() {
  const isDa = useIsDanish();

  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: isDa ? "System32 Cases" : "System32 Use Cases",
      url: siteConfig.url("/use-cases"),
      description: isDa ? "Fortrolige kundecases om cloud migration, governance, identitet, API-centralisering og event-driven arkitektur." : "Confidential client delivery patterns covering Azure governance, migration, access governance, API centralisation, and event-driven architecture.",
    },
  ];

  return (
    <>
      <Seo
        title={isDa ? "Cases | System32" : "Use Cases | System32"}
        description={isDa ? "Se hvordan System32 hjælper med cloud migration, governance, identitet, API'er, DevOps og komplekse platforme." : "Explore real Microsoft platform delivery stories across Azure migration, governance, identity, and DevOps transformation."}
        canonical={siteConfig.url("/use-cases")}
        type="website"
        schema={schema}
      />
      <style>{`
        .uc-page {
          min-height: 100vh;
          background:
            radial-gradient(ellipse 900px 600px at 5% 20%, rgba(37,99,235,0.08), transparent 60%),
            radial-gradient(ellipse 700px 500px at 95% 70%, rgba(124,58,237,0.08), transparent 60%),
            #f1f5f9;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif;
          padding: 72px 24px 80px;
        }
        .uc-header {
          text-align: center;
          max-width: 760px;
          margin: 0 auto 60px;
        }
        .uc-eyebrow {
          display: inline-block;
          color: #2563eb;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          margin-bottom: 12px;
        }
        .uc-header h1 {
          font-size: clamp(2rem, 4vw, 3rem);
          font-weight: 800;
          color: #0f1b4d;
          letter-spacing: -0.03em;
          margin: 0 0 16px;
          line-height: 1.15;
        }
        .uc-header p {
          color: #64748b;
          font-size: 1.1rem;
          line-height: 1.7;
          margin: 0;
        }
        .uc-proof-note {
          max-width: 780px;
          margin: -36px auto 42px;
          padding: 16px 20px;
          border-radius: 18px;
          background: rgba(255,255,255,0.78);
          border: 1px solid rgba(15,23,42,0.08);
          color: #475569;
          font-size: 0.94rem;
          line-height: 1.65;
          box-shadow: 0 4px 18px rgba(2,6,23,0.04);
        }
        .uc-proof-note strong {
          color: #0f1b4d;
        }

        .uc-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
          gap: 28px;
          max-width: 1200px;
          margin: 0 auto;
        }
        @media (max-width: 480px) { .uc-grid { grid-template-columns: 1fr; } }

        .uc-card {
          background: white;
          border-radius: 24px;
          border: 1px solid rgba(15,23,42,0.08);
          box-shadow: 0 4px 24px rgba(2,6,23,0.06);
          display: flex;
          flex-direction: column;
          overflow: hidden;
          transition: transform 0.18s, box-shadow 0.18s;
          text-decoration: none;
          color: inherit;
        }
        .uc-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 16px 48px rgba(2,6,23,0.12);
        }

        .uc-card-top {
          padding: 28px 28px 0;
        }
        .uc-category {
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.10em;
          text-transform: uppercase;
          color: #7c3aed;
          margin-bottom: 10px;
        }
        .uc-card h2 {
          font-size: 1.15rem;
          font-weight: 800;
          color: #0f1b4d;
          letter-spacing: -0.02em;
          margin: 0 0 12px;
          line-height: 1.35;
        }
        .uc-card-summary {
          font-size: 0.93rem;
          color: #64748b;
          line-height: 1.65;
          margin: 0 0 20px;
        }

        .uc-metrics {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          padding: 0 28px 20px;
        }
        .uc-metric {
          background: #f8fafc;
          border-radius: 14px;
          padding: 12px 14px;
          border: 1px solid rgba(15,23,42,0.06);
        }
        .uc-metric-value {
          font-size: 1.4rem;
          font-weight: 800;
          color: #0f1b4d;
          letter-spacing: -0.03em;
          line-height: 1;
        }
        .uc-metric-label {
          font-size: 0.73rem;
          color: #94a3b8;
          margin-top: 4px;
          font-weight: 500;
        }

        .uc-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 0 28px 20px;
        }
        .uc-tag {
          font-size: 0.72rem;
          font-weight: 600;
          padding: 4px 10px;
          border-radius: 100px;
        }

        .uc-card-footer {
          margin-top: auto;
          padding: 16px 28px 22px;
          border-top: 1px solid rgba(15,23,42,0.06);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .uc-read-more {
          font-size: 0.88rem;
          font-weight: 700;
          color: #2563eb;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .uc-tech-count {
          font-size: 0.78rem;
          color: #94a3b8;
          font-weight: 500;
        }

        .uc-cta {
          max-width: 1200px;
          margin: 26px auto 0;
          display: flex;
          justify-content: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        .uc-cta a {
          text-decoration: none;
          border-radius: 12px;
          padding: 12px 20px;
          font-weight: 700;
          font-size: 0.92rem;
        }
        .uc-cta-primary {
          color: white;
          background: linear-gradient(135deg, #2563eb, #7c3aed);
        }
        .uc-cta-secondary {
          color: #0f1b4d;
          border: 1px solid rgba(15,23,42,0.18);
          background: #fff;
        }
      `}</style>

      <div className="uc-page">
        <div className="uc-header">
          <span className="uc-eyebrow">{isDa ? "Kundecases" : "Client stories"}</span>
          <h1>{isDa ? <>Reelle problemer.<br />Løsninger der kan drives.</> : <>Real problems.<br />Real Microsoft solutions.</>}</h1>
          <p>
            {isDa
              ? "Her er eksempler på den type problemer System32 løser: cloud migration, identity consolidation, AI governance, API-centralisering, event-driven platforme og fejlfinding på tværs af komplekse systemer."
              : "These are the kinds of problems System32 solves: cloud migrations, identity consolidation, AI governance, API centralisation, event-driven platforms, and complex cross-platform troubleshooting."}
          </p>
        </div>

        <div className="uc-proof-note">
          <strong>{isDa ? "Alle kundenavne er udeladt af hensyn til fortrolighed." : "All client names are withheld for confidentiality."}</strong>{" "}
          {isDa
            ? "Branche, skala, teknologier og resultater vises som repræsentative leverancemønstre. Følsomme driftsdetaljer er generaliseret hvor nødvendigt."
            : "Sector, scale, technologies, and outcomes are presented as representative delivery patterns, with sensitive operational details generalised where required."}
        </div>

        <div className="uc-grid">
          {useCasesData.map(uc => {
            const displayUc = isDa && useCasesDa[uc.slug]
              ? { ...uc, ...useCasesDa[uc.slug] }
              : uc;

            return (
            <Link key={uc.slug} to={`/use-cases/${uc.slug}`} className="uc-card">
              <div className="uc-card-top">
                <div className="uc-category">{displayUc.category}</div>
                <h2>{displayUc.title}</h2>
                <p className="uc-card-summary">{displayUc.summary}</p>
              </div>

              <div className="uc-metrics">
                {displayUc.metrics.map(m => (
                  <div key={m.label} className="uc-metric">
                    <div className="uc-metric-value">{m.value}</div>
                    <div className="uc-metric-label">{m.label}</div>
                  </div>
                ))}
              </div>

              <div className="uc-tags">
                {uc.tags.map(t => (
                  <span
                    key={t}
                    className="uc-tag"
                    style={{ background: tagColors[t] || "#f1f5f9", color: tagText[t] || "#475569" }}
                  >
                    {t}
                  </span>
                ))}
              </div>

              <div className="uc-card-footer">
                <span className="uc-read-more">{isDa ? "Læs hele casen →" : "Read full story →"}</span>
                <span className="uc-tech-count">{isDa ? `${uc.techStack.length} teknologier brugt` : `${uc.techStack.length} technologies used`}</span>
              </div>
            </Link>
            );
          })}
        </div>

        <div className="uc-cta">
          <a href="/#contact-section" className="uc-cta-primary">{isDa ? "Har I et lignende problem? Book en strategisamtale" : "Have a similar problem? Book a strategy call"}</a>
          <Link to="/services/azure-cloud" className="uc-cta-secondary">{isDa ? "Se ydelser" : "Explore services"}</Link>
        </div>
      </div>
    </>
  );
}
